<template>
  <div
    :class="menuItemClass"
    :style="activeTabColor"
    class="menu-item"
  >
    <component
      :is="componentLink"
      :class="linkClass"
      :style="linkStyle"
      class="menu-item__link"
      colorless
      v-bind="attrsLink"
      @click="$emit('click-menu-item', item)"
    >
      {{ item.title }}
    </component>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { removeBurninghutFromUrl } from '~/utils/link';
  import type { ILink, TTheme } from '~/components/ArrowMenu/ArrowMenu';

  export default defineNuxtComponent({
    name: 'ArrowMenuLink',
    props: {
      item: {
        type: Object as PropType<ILink>,
        required: true,
      },

      theme: {
        type: String as PropType<TTheme>,
        default: 'light',
      },
    },

    computed: {
      isActive(): boolean {
        return this.$route.path === removeBurninghutFromUrl(this.item.link || '');
      },

      isDarkTheme(): boolean {
        return this.theme === 'dark';
      },

      menuItemClass(): Record<string, any> {
        const className = `menu-item`;
        const theme = this.theme;
        return {
          [`${className}--${theme}`]: theme,
          [`${className}--active`]: this.isActive,
          [`${className}--${theme}--active`]: this.isActive,
        };
      },

      linkClass(): Record<string, any> {
        const className = 'menu-item__link';
        const theme = this.theme;
        return {
          [`${className}--${theme}`]: theme,
          [`${className}--${theme}--colored`]: this.item.color_picker,
        };
      },

      componentLink(): string {
        return !this.isActive ? 'a' : 'span';
      },

      attrsLink(): Record<string, any> {
        return {
          [`href`]: !this.isActive ? removeBurninghutFromUrl(this.item.link) : null,
        };
      },

      activeTabColor(): Record<string, string> | null {
        if (this.isActive && !this.isDarkTheme) {
          return {
            '--active-color': this.item?.color_archive || '#ebebeb',
          };
        }
        return null;
      },

      linkStyle(): Record<string, any> | null {
        return {
          ...(this.linkColor && { 'background-color': this.linkColor }),
          '--text-color': this.textColor,
        };
      },

      linkColor(): string | undefined | null {
        return this.item.color_picker;
      },

      textColor(): string {
        return this.item.color_text || (this.isDarkTheme ? '#ccc' : '#000');
      },
    },
  });
</script>

<style lang="scss" scoped>
  @mixin active-rubric {
    &--active {
      background-color: var(--active-color);
    }
  }

  @mixin colored-link--dark {
    &--colored {
      position: relative;
      color: #fff;

      &::before {
        position: absolute;
        content: '';
        left: 0;
        top: 0;
        border-radius: 14px;
        height: 100%;
        width: 100%;
        opacity: 0;
        background: black;
        transition: opacity 0.2s ease-in;
        pointer-events: none;
      }

      @include hover-nested('&::before') {
        opacity: 0.1;
      }
    }
  }

  @mixin link--dark {
    &--dark {
      @include hover {
        color: #ffffff;
      }
      @include colored-link--dark;
    }
  }

  @mixin item--light {
    &--light {
      transition: background-color 0.2s ease-in;
      @include active-rubric;
      @include hover {
        background-color: #e4fcfb;
      }
    }
  }

  .menu-item {
    position: relative;
    padding: 0;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    @include item--light;

    &--active {
      pointer-events: none;
    }

    &__link {
      height: 100%;
      padding: $spacing-1 $spacing-3;
      white-space: nowrap;
      text-decoration: none;
      font-family: $text-font--1;
      border-radius: 14px;
      @include normalText;
      font-weight: 500;
      color: var(--text-color);

      display: flex;
      align-items: center;

      @include link--dark;
    }
  }
</style>
